import React, { useRef, useState } from "react";

/**
 * A <button> that shows an inline spinner *on itself* while its async onClick runs, and
 * reverts automatically when the promise settles (success or error). Use this for any action
 * that hits an API so the user sees the exact button they clicked working.
 *
 * Props: same as a native button, plus:
 *   - onClick: may return a promise; the button stays in the loading state until it settles.
 *   - loadingText: optional text to show beside the spinner while loading (defaults to children).
 *   - spinnerClassName: optional class for the spinner (colour is inherited via currentColor).
 */

// Inject the spin keyframes once (shared by every AsyncButton).
if (typeof document !== "undefined" && !document.getElementById("zbz-btn-spin-kf")) {
  const el = document.createElement("style");
  el.id = "zbz-btn-spin-kf";
  el.textContent = "@keyframes zbz-btn-spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(el);
}

const Spinner = ({ size = 14 }) => (
  <span
    aria-hidden="true"
    style={{
      width: size,
      height: size,
      border: "2px solid currentColor",
      borderTopColor: "transparent",
      borderRadius: "50%",
      display: "inline-block",
      animation: "zbz-btn-spin .7s linear infinite",
      flex: "0 0 auto",
    }}
  />
);

const AsyncButton = ({ onClick, children, disabled, loadingText, className, type = "button", ...rest }) => {
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  // Track mount so we never setState after unmount (e.g. the panel reloads on success).
  React.useEffect(() => () => { mounted.current = false; }, []);

  const handle = async (e) => {
    if (loading || disabled) return;
    try {
      setLoading(true);
      await onClick?.(e);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  return (
    // eslint-disable-next-line react/button-has-type
    <button {...rest} type={type} className={className} disabled={disabled || loading} onClick={handle}>
      {loading ? (
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <Spinner />
          {loadingText ?? children}
        </span>
      ) : (
        children
      )}
    </button>
  );
};

export default AsyncButton;
